import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import type { KitchenOrder, OrderStatus } from '../lib/types.ts';

interface UseKitchenRealtimeOptions {
  tenantSlug: string;
  locationLocalId: string;
  enabled: boolean;
}

export function useKitchenRealtime({ tenantSlug, locationLocalId, enabled }: UseKitchenRealtimeOptions) {
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['kitchen-orders', tenantSlug, locationLocalId],
    });
  }, [queryClient, tenantSlug, locationLocalId]);

  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel(`kitchen:${tenantSlug}:${locationLocalId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as { status?: OrderStatus } | null;
          const activeStatuses: OrderStatus[] = ['new', 'accepted', 'cooking', 'ready'];
          const oldStatus = (payload.old as { status?: OrderStatus } | null)?.status;
          const newStatus = row?.status;

          // Refetch if the changed order touches any active status.
          const relevant =
            (oldStatus && activeStatuses.includes(oldStatus)) ||
            (newStatus && activeStatuses.includes(newStatus));

          if (relevant) invalidate();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'menu_item_availability',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['menu'] });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [enabled, tenantSlug, locationLocalId, invalidate, queryClient]);
}

export function applyOptimisticStatus(
  orders: KitchenOrder[],
  orderId: string,
  newStatus: OrderStatus,
): KitchenOrder[] {
  const active: OrderStatus[] = ['new', 'accepted', 'cooking', 'ready'];
  return orders
    .map((o) => (o.order_id === orderId ? { ...o, status: newStatus } : o))
    .filter((o) => active.includes(o.status));
}
